import React, { useState } from 'react';

const EligibilityChecker = ({ onOpenModal }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({ 1: null, 2: null, 3: null });
  const [showResult, setShowResult] = useState(false);

  const handleSelect = (questionNumber, value) => {
    const updatedAnswers = { ...answers, [questionNumber]: value };
    setAnswers(updatedAnswers);

    if (questionNumber < 3) {
      setStep(questionNumber + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleReset = () => {
    setAnswers({ 1: null, 2: null, 3: null });
    setStep(1);
    setShowResult(false);
  };

  // Logic to calculate result text
  const getResultData = () => {
    const lic = answers[1];
    const rev = answers[2];
    const reg = answers[3];

    let cls = '';
    let tag = '';
    let head = '';
    let text = '';
    let rec = '';

    if (lic === 'offshore') {
      cls = 'warn';
      tag = 'Needs a closer look';
      head = 'It depends on whether you have a UAE presence.';
      text =
        'A company licensed outside the UAE is generally only in scope if it has a permanent establishment here, a UAE source of income, or is effectively managed from the UAE. That is a judgement call, and it is worth twenty minutes to settle it properly.';
      rec =
        '<b>Recommended: a review call first</b>We will not quote a filing fee until we know whether you are in scope at all.';
    } else {
      const zone = lic === 'freezone';
      if (reg === 'no' || reg === 'unsure') {
        cls = 'act';
        tag = 'Act now';
        head = 'You need to register, and registration is the part with the AED 10,000 penalty.';
        text =
          'Every UAE licensed company registers for Corporate Tax and files a return, ' +
          (rev === 'none'
            ? 'including dormant companies with no revenue at all. '
            : 'even when the tax due works out at nil. ') +
          (reg === 'unsure'
            ? 'We can check your registration status on the FTA portal with you on the call.'
            : 'The sooner this is done, the smaller the exposure.');
      } else {
        cls = 'ok';
        tag = 'On track';
        head = 'You are registered. The return is the remaining piece.';
        text =
          'With a Tax Registration Number in place, what is left is preparing and filing the return by your deadline, which is nine months after your financial year end.';
      }

      if (rev === 'high') {
        rec =
          '<b>Likely plan: Filing and tax payable, AED 1,500 plus VAT</b>' +
          'Once revenue has passed AED 3 million in any period you fall outside Small Business Relief permanently, so the tax has to be computed and paid. ' +
          (zone ? 'As a free zone company you may still reach 0% on qualifying income, which we would assess.' : '');
      } else if (rev === 'none') {
        rec =
          '<b>Likely plan: Filing only, AED 750 plus VAT</b>' +
          'A dormant company still registers and still files. The return is short, and the fee reflects that.';
      } else {
        rec =
          '<b>Likely plan: Filing only, AED 750 plus VAT</b>' +
          'If revenue has never exceeded AED 3 million in this or any earlier period, you can usually elect Small Business Relief and be treated as having no taxable income. ' +
          'The election is not automatic, it has to be made in the return, and the relief runs only to 31 December 2026. ' +
          (zone
            ? 'A free zone company can elect Small Business Relief only if it is NOT claiming Qualifying Free Zone Person status. The two are mutually exclusive under Ministerial Decision No. 73 of 2023, and we work out which is worth more to you on the call.'
            : '');
      }
    }

    return { cls, tag, head, text, rec };
  };

  const result = showResult ? getResultData() : null;

  return (
    <section className="sec" id="register">
      <div className="shell">
        <div className="check">
          <div className="check-copy rv">
            <h2 className="h2">Do you have to register for UAE Corporate Tax?</h2>
            <p className="answer">
              Yes, in almost every case. Any company holding a UAE trade licence must register for Corporate Tax with the
              Federal Tax Authority and file a return, even when the tax due is nil and even when the company did not trade
              at all. The AED 10,000 penalty attaches to registering late, not to owing tax.
            </p>
            <p className="support">Three points catch people out more than any others:</p>
            <ul style={{ listStyle: 'none', margin: '16px 0 0', padding: 0, display: 'grid', gap: '11px' }}>
              <li style={{ display: 'flex', gap: '11px', alignItems: 'flex-start', fontSize: '.95rem' }}>
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: '3px' }}>
                  <circle cx="12" cy="12" r="11" fill="#e1edf5" />
                  <path d="M7.5 12.4l3 3 6-6.4" stroke="#0a3d62" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>
                  <b>Free zone companies are not exempt.</b> A Qualifying Free Zone Person can reach 0% on qualifying income,
                  but registration and filing are still compulsory.
                </span>
              </li>
              <li style={{ display: 'flex', gap: '11px', alignItems: 'flex-start', fontSize: '.95rem' }}>
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: '3px' }}>
                  <circle cx="12" cy="12" r="11" fill="#e1edf5" />
                  <path d="M7.5 12.4l3 3 6-6.4" stroke="#0a3d62" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>
                  <b>Small Business Relief is cumulative, and it expires.</b> Revenue must have stayed at or below AED 3 million
                  in this and every earlier period. Exceed it once and the relief is gone for good. It runs only to 31 December 2026.
                </span>
              </li>
              <li style={{ display: 'flex', gap: '11px', alignItems: 'flex-start', fontSize: '.95rem' }}>
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: '3px' }}>
                  <circle cx="12" cy="12" r="11" fill="#e1edf5" />
                  <path d="M7.5 12.4l3 3 6-6.4" stroke="#0a3d62" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>
                  <b>A dormant company still has an obligation.</b> No income does not mean no return.
                </span>
              </li>
            </ul>
          </div>

          <div className="quiz rv" id="quiz">
            <div className="quiz-head">
              <h3>Check your position in three questions</h3>
              <div className="quiz-progress" aria-hidden="true">
                <i className={step >= 1 || showResult ? 'on' : ''}></i>
                <i className={step >= 2 || showResult ? 'on' : ''}></i>
                <i className={step >= 3 || showResult ? 'on' : ''}></i>
              </div>
            </div>
            <div className="quiz-body">
              {/* Step 1 */}
              {!showResult && step === 1 && (
                <div className="quiz-step on" data-step="1">
                  <p className="quiz-q">Where is your company licensed?</p>
                  <p className="quiz-sub">Question 1 of 3</p>
                  <div className="opts">
                    <button className="opt" type="button" onClick={() => handleSelect(1, 'mainland')}>
                      <span className="mark" aria-hidden="true">
                        <i></i>
                      </span>
                      UAE mainland
                    </button>
                    <button className="opt" type="button" onClick={() => handleSelect(1, 'freezone')}>
                      <span className="mark" aria-hidden="true">
                        <i></i>
                      </span>
                      A UAE free zone
                    </button>
                    <button className="opt" type="button" onClick={() => handleSelect(1, 'offshore')}>
                      <span className="mark" aria-hidden="true">
                        <i></i>
                      </span>
                      Outside the UAE
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2 */}
              {!showResult && step === 2 && (
                <div className="quiz-step on" data-step="2">
                  <p className="quiz-q">Has your revenue ever exceeded AED 3 million?</p>
                  <p className="quiz-sub">Question 2 of 3. Revenue, not profit, and in this or any earlier tax period.</p>
                  <div className="opts">
                    <button className="opt" type="button" onClick={() => handleSelect(2, 'low')}>
                      <span className="mark" aria-hidden="true">
                        <i></i>
                      </span>
                      No, never
                    </button>
                    <button className="opt" type="button" onClick={() => handleSelect(2, 'high')}>
                      <span className="mark" aria-hidden="true">
                        <i></i>
                      </span>
                      Yes, in at least one period
                    </button>
                    <button className="opt" type="button" onClick={() => handleSelect(2, 'none')}>
                      <span className="mark" aria-hidden="true">
                        <i></i>
                      </span>
                      No revenue at all, the company is dormant
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3 */}
              {!showResult && step === 3 && (
                <div className="quiz-step on" data-step="3">
                  <p className="quiz-q">Have you registered for Corporate Tax with the FTA?</p>
                  <p className="quiz-sub">Question 3 of 3</p>
                  <div className="opts">
                    <button className="opt" type="button" onClick={() => handleSelect(3, 'yes')}>
                      <span className="mark" aria-hidden="true">
                        <i></i>
                      </span>
                      Yes, we have our Tax Registration Number
                    </button>
                    <button className="opt" type="button" onClick={() => handleSelect(3, 'no')}>
                      <span className="mark" aria-hidden="true">
                        <i></i>
                      </span>
                      No, not yet
                    </button>
                    <button className="opt" type="button" onClick={() => handleSelect(3, 'unsure')}>
                      <span className="mark" aria-hidden="true">
                        <i></i>
                      </span>
                      I am not sure
                    </button>
                  </div>
                </div>
              )}

              {/* Results */}
              {showResult && result && (
                <div className="quiz-result on" id="quizResult" role="status" aria-live="polite" style={{ display: 'block' }}>
                  <span className={`res-badge ${result.cls}`} id="resBadge">
                    {result.tag}
                  </span>
                  <h4 id="resTitle">{result.head}</h4>
                  <p id="resBody">{result.text}</p>
                  <div className="res-plan" id="resPlan" dangerouslySetInnerHTML={{ __html: result.rec }} />
                  <div className="quiz-actions">
                    <button className="btn js-open-modal" onClick={onOpenModal} aria-label="Book a free review about your eligibility result">
                      Book a free review
                    </button>
                    <button className="link-btn" type="button" id="quizReset" onClick={handleReset}>
                      Start again
                    </button>
                  </div>
                  <p className="quiz-legal">
                    General guidance based on three answers, not tax advice. Your actual position depends on your licence, your
                    tax period and the elections made in the return. We confirm it in writing on the call.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EligibilityChecker;
